import { useState, useEffect, useRef } from "react";
import { useAtomValue } from "jotai";

import { useClassNames } from "@/utils/classNames";
import { useDoubleTap } from "@/utils/hooks/useDoubleTap";
import { videoPlayerAtom } from "@/constants/atoms";

import { Text } from "../Text";
import { Icon, StreamingIcons } from "../Icon";
import { SkipButtonProps, SkipDirection } from "./types";
import styles from "./SkipButton.module.scss";

const DOUBLE_CLICK_DELAY = 300; // ms
const HIDE_INDICATOR_DELAY = 1000; // ms
const RESET_INDICATOR_DELAY = 150; // ms

/**
 * Skip button for video player controls (forward or backward)
 * Requires double tap on button area, single tap on icon
 *
 * @param props - The button properties
 * @returns the skip button component
 */
const SkipButton = ({
  direction,
  seconds,
  onClick,
  ariaLabel,
  className,
}: SkipButtonProps) => {
  const [skipCount, setSkipCount] = useState(0);
  const [showIndicator, setShowIndicator] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);
  const videoPlayerState = useAtomValue(videoPlayerAtom);

  const defaultLabel =
    direction === SkipDirection.Forward
      ? `Skip forward ${seconds} seconds`
      : `Skip backward ${seconds} seconds`;

  const cn = useClassNames(styles, styles["skip-button"], className);

  const tapAreaCn = useClassNames(
    styles,
    styles["tap-area"],
    styles[direction],
    className,
  );

  const indicatorCn = useClassNames(
    styles,
    styles["skip-indicator"],
    styles[direction],
    showIndicator && styles["show"],
  );

  const icon =
    direction === SkipDirection.Backward
      ? StreamingIcons.Back10sec
      : StreamingIcons.Fwd10sec;

  const handleSkip = (event: React.MouseEvent) => {
    onClick();

    /** Reset controls timer without dismissing controls */
    if (videoPlayerState.resetControlsTimer) {
      videoPlayerState.resetControlsTimer();
    }

    /** Update skip count */
    setSkipCount((prev) => prev + seconds);
    setShowIndicator(true);

    /** Clear existing timers */
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
    }

    /** Hide indicator after 1 second of no skips */
    timerRef.current = setTimeout(() => {
      setShowIndicator(false);

      /** Reset count after fade-out animation completes */
      resetTimerRef.current = setTimeout(() => {
        setSkipCount(0);
      }, RESET_INDICATOR_DELAY);
    }, HIDE_INDICATOR_DELAY);
  };

  /** Cleanup timers on unmount */
  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    },
    [],
  );

  /** Double tap handler using custom hook */
  const { onClick: handleTapAreaClick } = useDoubleTap({
    delay: DOUBLE_CLICK_DELAY,
    onDoubleTap: handleSkip,
  });

  /** Single tap handler for icon */
  const handleIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleSkip(event);
  };

  const skipText =
    direction === SkipDirection.Forward ? `+${skipCount}` : `-${skipCount}`;

  return (
    <button className={cn} aria-label={ariaLabel || defaultLabel} type="button">
      <Icon className={styles.icon} icon={icon} onClick={handleIconClick} />
      <div className={indicatorCn}>
        <Text variant="p" className={styles["skip-text"]}>
          {skipText}
        </Text>
      </div>
      <div
        className={tapAreaCn}
        onClick={handleTapAreaClick}
        data-tap-area="true"
      />
    </button>
  );
};

export { SkipButton };
